import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Spinner, Badge, Card, Carousel, Row, Col } from 'react-bootstrap';
import {
  FaBed, FaBath, FaCar, FaRulerCombined, FaMapMarkerAlt, FaHome,
  FaCalendarAlt, FaSwimmingPool, FaEye, FaTools, FaBuilding,
  FaPhone, FaEnvelope, FaCheck, FaStar, FaArrowUp, FaArrowDown, FaMinus,
  FaLightbulb, FaDollarSign, FaChevronDown, FaChevronUp, FaChartLine, FaClock,
  FaSchool, FaShoppingCart, FaTree, FaThumbsUp, FaThumbsDown, FaExclamationTriangle, FaRoute,
  FaUsers, FaTrophy, FaAward,
} from 'react-icons/fa';
import { handleTokenAndPostRequest, formatCurrency, setMetaTags, trackEvent } from '../utils/utils';
import AppraisalMap from '../components/AppraisalMap';
import './Styles.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmt = (v) => {
  if (v == null) return 'N/A';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return v;
  return `$${n.toLocaleString('en-AU')}`;
};

const fmtWeekly = (v) => {
  if (v == null) return 'N/A';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return v;
  return `$${n.toLocaleString('en-AU')}/wk`;
};

const parseJsonField = (field) => {
  if (!field) return null;
  if (typeof field === 'string') {
    try { return JSON.parse(field); } catch { return null; }
  }
  return field;
};

function deriveSuburbId(address) {
  if (!address) return '';
  const m = address.match(/([A-Za-z\s]+?)\s+(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\s+(\d{4})\s*$/i);
  if (m) {
    const suburb = m[1].trim().toLowerCase().replace(/\s+/g, '-');
    const state = m[2].toLowerCase();
    const postcode = m[3];
    return `${suburb}-${state}-${postcode}`;
  }
  return '';
}

const isLightColor = (hex) => {
  if (!hex) return false;
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // Perceived brightness (YIQ)
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
};

const darkenColor = (hex, amount = 0.5) => {
  if (!hex) return '#1a1a1a';
  const c = hex.replace('#', '');
  const r = Math.round(parseInt(c.substring(0, 2), 16) * (1 - amount));
  const g = Math.round(parseInt(c.substring(2, 4), 16) * (1 - amount));
  const b = Math.round(parseInt(c.substring(4, 6), 16) * (1 - amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const SectionHeading = ({ children, color }) => (
  <h5 style={{ fontWeight: 700, fontSize: '1.1rem', color: color || '#111', marginBottom: 16 }}>
    {children}
  </h5>
);

const MatchScoreBadge = ({ score }) => {
  if (score == null) return null;
  const labels = ['', 'Low', 'Fair', 'Good', 'Strong', 'Excellent'];
  const colors = ['', '#ef4444', '#f59e0b', '#22c55e', '#10b981', '#059669'];
  const bgColors = ['', '#fef2f2', '#fffbeb', '#f0fdf4', '#ecfdf5', '#ecfdf5'];
  const s = Math.max(1, Math.min(5, Math.round(score)));
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 20,
      backgroundColor: bgColors[s],
      border: `1px solid ${colors[s]}20`,
    }}>
      <div style={{ display: 'flex', gap: 2 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <FaStar key={i} size={9} style={{ color: i <= s ? colors[s] : '#e5e7eb' }} />
        ))}
      </div>
      <span style={{ fontSize: '0.68rem', fontWeight: 600, color: colors[s], letterSpacing: '0.02em' }}>
        {labels[s]} Match
      </span>
    </div>
  );
};

const DiffChip = ({ label, value, unit }) => {
  if (value == null) return null;
  const isZero = value === 0;
  const isPositive = value > 0;
  const color = isZero ? '#6b7280' : isPositive ? '#16a34a' : '#dc2626';
  const bg = isZero ? '#f3f4f6' : isPositive ? '#f0fdf4' : '#fef2f2';
  const icon = isZero ? <FaMinus size={7} /> : isPositive ? <FaArrowUp size={7} /> : <FaArrowDown size={7} />;
  const text = isZero ? `Same ${label}` : `${isPositive ? '+' : ''}${value}${unit || ''} ${label}`;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: '0.7rem', fontWeight: 500, color,
      backgroundColor: bg, padding: '3px 9px', borderRadius: 12,
    }}>
      {icon} {text}
    </span>
  );
};

const ComparableCard = ({ comp, textAccentColor, isMobile }) => {
  if (!comp) return null;
  const analysis = comp.ai_analysis;
  const diff = analysis?.diff;
  const allImages = (comp.photo_urls && comp.photo_urls.length > 0)
    ? comp.photo_urls
    : [comp.photo_url_main || comp.photo_url].filter(Boolean);
  return (
    <div style={{
      marginBottom: 20,
      borderRadius: 16,
      overflow: 'hidden',
      backgroundColor: '#fff',
      boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    }}>
      {allImages.length > 0 && (
        <div style={{ position: 'relative' }}>
          {allImages.length > 1 ? (
            <Carousel interval={null} wrap={false} indicators={false} controls>
              {allImages.map((url, idx) => (
                <Carousel.Item key={idx}>
                  <img
                    src={url}
                    alt={`${comp.address || comp.display_address || 'Comparable'} ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: isMobile ? 200 : 220,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Carousel.Item>
              ))}
            </Carousel>
          ) : (
            <img
              src={allImages[0]}
              alt={comp.address || comp.display_address || 'Comparable'}
              style={{
                width: '100%',
                height: isMobile ? 200 : 220,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          )}
          {analysis?.score != null && (
            <div style={{ position: 'absolute', top: 12, right: 12 }}>
              <MatchScoreBadge score={analysis.score} />
            </div>
          )}
        </div>
      )}
      <div style={{ padding: isMobile ? '16px 18px' : '20px 24px' }}>
        {allImages.length === 0 && analysis?.score != null && (
          <div style={{ marginBottom: 10 }}>
            <MatchScoreBadge score={analysis.score} />
          </div>
        )}
        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a1a1a', marginBottom: 6, letterSpacing: '-0.01em' }}>
          {comp.address || comp.display_address || `Comparable ${comp.id}`}
        </div>
        {comp.display_price ? (
          <div style={{ fontWeight: 700, fontSize: '1.15rem', color: textAccentColor, marginBottom: 8 }}>
            {comp.display_price}
          </div>
        ) : (comp.price || comp.sold_price) ? (
          <div style={{ fontWeight: 700, fontSize: '1.15rem', color: textAccentColor, marginBottom: 8 }}>
            {fmt(comp.price || comp.sold_price)}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: '0.82rem', color: '#9ca3af', marginBottom: (diff || analysis?.summary || comp.comment) ? 10 : 0 }}>
          {comp.bedrooms != null && <span><FaBed size={12} style={{ marginRight: 4 }} />{comp.bedrooms}</span>}
          {comp.bathrooms != null && <span><FaBath size={12} style={{ marginRight: 4 }} />{comp.bathrooms}</span>}
          {comp.carspaces != null && <span><FaCar size={12} style={{ marginRight: 4 }} />{comp.carspaces}</span>}
          {comp.land_area != null && (typeof comp.land_area === 'object' ? comp.land_area.size > 0 : comp.land_area > 0) && <span><FaRulerCombined size={11} style={{ marginRight: 4 }} />{typeof comp.land_area === 'object' ? `${comp.land_area.size}${comp.land_area.unit}` : `${comp.land_area}m²`}</span>}
          {(comp.sold_date || comp.sold_timestamp) && <span><FaCalendarAlt size={11} style={{ marginRight: 4 }} />{comp.sold_date || new Date(comp.sold_timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
        </div>
        {diff && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: (analysis?.summary || comp.comment) ? 12 : 0 }}>
            {diff.bedrooms != null && <DiffChip label="beds" value={diff.bedrooms} />}
            {diff.bathrooms != null && <DiffChip label="baths" value={diff.bathrooms} />}
            {diff.parking != null && <DiffChip label="parking" value={diff.parking} />}
            {diff.land_size != null && diff.land_size !== 0 && <DiffChip label="land" value={diff.land_size} unit="m²" />}
          </div>
        )}
        {analysis?.summary && (
          <p style={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.65, margin: 0, borderTop: '1px solid #f3f4f6', paddingTop: 12, marginBottom: comp.comment ? 10 : 0 }}>
            {analysis.summary}
          </p>
        )}
        {comp.comment && (
          <p style={{ fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.6, margin: 0, fontStyle: 'italic', borderTop: '1px solid #f3f4f6', paddingTop: 10 }}>
            "{comp.comment}"
          </p>
        )}
      </div>
    </div>
  );
};

const effortColors = { low: '#16a34a', medium: '#f59e0b', high: '#dc2626' };
const effortBg = { low: '#f0fdf4', medium: '#fffbeb', high: '#fef2f2' };
const effortLabels = { low: 'Easy', medium: 'Medium', high: 'Hard' };

const ImprovementItem = ({ item }) => {
  if (!item) return null;
  const effort = item.effort || 'medium';
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start',
      padding: '12px 0',
      borderBottom: '1px solid #f5f3f0',
    }}>
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        <FaCheck size={10} style={{ color: effortColors[effort] }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a1a', marginBottom: 4 }}>
          {item.action}
        </div>
        <div style={{ fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.5 }}>
          {item.value_impact}
        </div>
      </div>
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a1a' }}>{item.estimated_cost}</div>
        <span style={{
          display: 'inline-block', fontSize: '0.65rem', fontWeight: 600,
          color: effortColors[effort], backgroundColor: effortBg[effort],
          padding: '2px 8px', borderRadius: 10, marginTop: 3,
        }}>
          {effortLabels[effort] || effort}
        </span>
      </div>
    </div>
  );
};

const ImageImprovementCard = ({ imageUrl, data, index, accentColor, textAccentColor, isMobile }) => {
  const [expanded, setExpanded] = useState(true);
  if (!data) return null;

  const quickWins = Array.isArray(data.quick_wins) ? data.quick_wins : [];
  const moderateUpgrades = Array.isArray(data.moderate_upgrades) ? data.moderate_upgrades : [];
  const majorImprovements = Array.isArray(data.major_improvements) ? data.major_improvements : [];
  const allItems = [...quickWins, ...moderateUpgrades, ...majorImprovements];
  if (allItems.length === 0 && !data.overall_assessment) return null;

  return (
    <div style={{
      marginBottom: 20, borderRadius: 20, overflow: 'hidden',
      backgroundColor: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
    }}>
      {/* Image Header */}
      <div style={{ position: 'relative' }}>
        <img
          src={imageUrl}
          alt={`Area ${index + 1}`}
          style={{
            width: '100%', height: isMobile ? 200 : 280,
            objectFit: 'cover', display: 'block',
          }}
        />
        {/* Gradient overlay */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
          pointerEvents: 'none',
        }} />
        {/* Summary badges on image */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: isMobile ? '12px 16px' : '16px 24px',
          display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.estimated_total_investment && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                backgroundColor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
                padding: '5px 12px', borderRadius: 20,
                fontSize: '0.75rem', fontWeight: 600, color: '#1a1a1a',
              }}>
                <FaDollarSign size={10} style={{ color: accentColor }} /> Invest {data.estimated_total_investment}
              </span>
            )}
            {data.estimated_value_uplift && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                backgroundColor: 'rgba(22,163,74,0.9)', backdropFilter: 'blur(8px)',
                padding: '5px 12px', borderRadius: 20,
                fontSize: '0.75rem', fontWeight: 600, color: '#fff',
              }}>
                <FaArrowUp size={9} /> {data.estimated_value_uplift} uplift
              </span>
            )}
          </div>
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
              border: 'none', borderRadius: 20, padding: '5px 12px',
              fontSize: '0.75rem', fontWeight: 600, color: '#1a1a1a',
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            {expanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      {/* Expandable content */}
      {expanded && (
        <div style={{ padding: isMobile ? '20px 18px' : '24px 28px' }}>
          {/* Overall Assessment */}
          {data.overall_assessment && (
            <p style={{
              fontSize: '0.88rem', color: '#4b5563', lineHeight: 1.7,
              margin: '0 0 20px', padding: '0 0 16px',
              borderBottom: '1px solid #f0eeeb',
            }}>
              {data.overall_assessment}
            </p>
          )}

          {/* Quick Wins */}
          {quickWins.length > 0 && (
            <div style={{ marginBottom: moderateUpgrades.length || majorImprovements.length ? 20 : 0 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: '0.72rem', fontWeight: 600, color: '#16a34a',
                textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8,
              }}>
                <FaLightbulb size={11} /> Quick Wins
              </div>
              {quickWins.map((item, i) => <ImprovementItem key={i} item={item} />)}
            </div>
          )}

          {/* Moderate Upgrades */}
          {moderateUpgrades.length > 0 && (
            <div style={{ marginBottom: majorImprovements.length ? 20 : 0 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: '0.72rem', fontWeight: 600, color: '#f59e0b',
                textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8,
              }}>
                <FaTools size={11} /> Moderate Upgrades
              </div>
              {moderateUpgrades.map((item, i) => <ImprovementItem key={i} item={item} />)}
            </div>
          )}

          {/* Major Improvements */}
          {majorImprovements.length > 0 && (
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: '0.72rem', fontWeight: 600, color: '#dc2626',
                textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8,
              }}>
                <FaBuilding size={11} /> Major Improvements
              </div>
              {majorImprovements.map((item, i) => <ImprovementItem key={i} item={item} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TrendStat = ({ icon, label, value, changeText, delta, accentColor }) => {
  if (value == null) return null;
  const changeColor = changeText === '0%' || changeText == null ? '#9ca3af'
    : delta > 0 ? '#16a34a' : '#dc2626';
  return (
    <div style={{
      flex: 1, minWidth: 120, textAlign: 'center',
      padding: '20px 12px',
    }}>
      <div style={{ color: accentColor, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
        {value}
      </div>
      {changeText && (
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: changeColor, marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          {delta > 0 ? <FaArrowUp size={9} /> : delta < 0 ? <FaArrowDown size={9} /> : null}
          {changeText}
        </div>
      )}
    </div>
  );
};

const TrendsLocationSection = ({ suburbData, accentColor, textAccentColor, isMobile }) => {
  if (!suburbData) return null;

  const {
    suburb_initcap,
    state,
    postcode,
    suburb_summary,
    median_sold_price_filled,
    median_sold_days_on_market_filled,
    sold_properties,
    prior_median_sold_price,
    prior_median_sold_days_on_market,
    prior_sold_properties,
    median_sold_price_percent_change_text,
    median_sold_days_on_market_percent_change_text,
    sold_properties_percent_change_text,
    median_rent_price_filled,
    median_rent_days_on_market_filled,
    rent_properties,
    prior_median_rent_price,
    prior_median_rent_days_on_market,
    prior_rent_properties,
    median_rent_price_percent_change_text,
    median_rent_days_on_market_percent_change_text,
    rent_properties_percent_change_text,
    reporting_month,
  } = suburbData;

  const fmtPrice = (v) => {
    if (v == null || typeof v !== 'number') return null;
    return `$${v.toLocaleString('en-AU')}`;
  };

  const calcDelta = (current, prior) => {
    if (current == null || prior == null || prior === 0) return null;
    return ((current - prior) / prior * 100).toFixed(0);
  };

  const soldPriceDelta = calcDelta(median_sold_price_filled, prior_median_sold_price);
  const soldDomDelta = calcDelta(median_sold_days_on_market_filled, prior_median_sold_days_on_market);
  const soldCountDelta = calcDelta(sold_properties, prior_sold_properties);
  const rentPriceDelta = calcDelta(median_rent_price_filled, prior_median_rent_price);
  const rentDomDelta = calcDelta(median_rent_days_on_market_filled, prior_median_rent_days_on_market);
  const rentCountDelta = calcDelta(rent_properties, prior_rent_properties);

  const hasSaleData = median_sold_price_filled != null || median_sold_days_on_market_filled != null || sold_properties != null;
  const hasRentData = median_rent_price_filled != null || median_rent_days_on_market_filled != null || rent_properties != null;

  if (!hasSaleData && !hasRentData && !suburb_summary) return null;

  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 20, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
        Trends & Location
      </div>

      {/* Suburb header & summary */}
      <div style={{
        borderRadius: 20, overflow: 'hidden', backgroundColor: '#fff',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)', marginBottom: 20,
      }}>
        <div style={{ padding: isMobile ? '24px 20px' : '32px 40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: suburb_summary ? 16 : 0 }}>
            <FaMapMarkerAlt size={14} style={{ color: accentColor }} />
            <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
              {suburb_initcap || 'Suburb'}
            </span>
            {(state || postcode) && (
              <span style={{ fontSize: '0.85rem', color: '#9ca3af', fontWeight: 400 }}>
                {[state, postcode].filter(Boolean).join(' ')}
              </span>
            )}
          </div>
          {suburb_summary && (
            <p style={{ fontSize: '0.9rem', color: '#4b5563', lineHeight: 1.75, margin: 0 }}>
              {suburb_summary}
            </p>
          )}
        </div>
      </div>

      {/* Sale Market Stats */}
      {hasSaleData && (
        <div style={{
          borderRadius: 20, overflow: 'hidden', backgroundColor: '#fff',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)', marginBottom: hasRentData ? 20 : 0,
        }}>
          <div style={{
            padding: isMobile ? '6px 16px' : '8px 24px',
            backgroundColor: '#1a1a1a',
          }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Sale Market — Last Month
            </span>
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap',
            borderTop: 'none',
          }}>
            <TrendStat
              icon={<FaDollarSign size={16} />}
              label="Median Price"
              value={fmtPrice(median_sold_price_filled)}
              changeText={median_sold_price_percent_change_text}
              delta={soldPriceDelta}
              accentColor={accentColor}
            />
            <TrendStat
              icon={<FaClock size={16} />}
              label="Days on Market"
              value={median_sold_days_on_market_filled}
              changeText={median_sold_days_on_market_percent_change_text}
              delta={soldDomDelta}
              accentColor={accentColor}
            />
            <TrendStat
              icon={<FaChartLine size={16} />}
              label="Properties Sold"
              value={sold_properties}
              changeText={sold_properties_percent_change_text}
              delta={soldCountDelta}
              accentColor={accentColor}
            />
          </div>
        </div>
      )}

      {/* Rental Market Stats */}
      {hasRentData && (
        <div style={{
          borderRadius: 20, overflow: 'hidden', backgroundColor: '#fff',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <div style={{
            padding: isMobile ? '6px 16px' : '8px 24px',
            backgroundColor: '#f5f3f0',
          }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Rental Market — Last Month
            </span>
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap',
          }}>
            <TrendStat
              icon={<FaDollarSign size={16} />}
              label="Median Rent"
              value={fmtPrice(median_rent_price_filled)}
              changeText={median_rent_price_percent_change_text}
              delta={rentPriceDelta}
              accentColor={accentColor}
            />
            <TrendStat
              icon={<FaClock size={16} />}
              label="Days on Market"
              value={median_rent_days_on_market_filled}
              changeText={median_rent_days_on_market_percent_change_text}
              delta={rentDomDelta}
              accentColor={accentColor}
            />
            <TrendStat
              icon={<FaChartLine size={16} />}
              label="Properties Rented"
              value={rent_properties}
              changeText={rent_properties_percent_change_text}
              delta={rentCountDelta}
              accentColor={accentColor}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Format an Australian mobile into the human-readable "04xx xxx xxx" form.
// Accepts E.164 ("+61421346175"), local ("0421346175"), or already-formatted
// inputs with spaces/dashes. Falls back to the input untouched for numbers
// we don't recognise.
const formatPhoneNumber = (raw) => {
  if (!raw) return raw;
  const digits = String(raw).replace(/[^\d+]/g, '');
  let local = null;
  // +61 followed by 9 subscriber digits -> drop +61, prefix with 0
  if (digits.startsWith('+61') && digits.length === 12) {
    local = '0' + digits.slice(3);
  } else if (digits.startsWith('61') && digits.length === 11) {
    local = '0' + digits.slice(2);
  } else if (digits.startsWith('0') && digits.length === 10) {
    local = digits;
  }
  if (local && local.length === 10) {
    return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7, 10)}`;
  }
  return raw;
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const AppraisalPage = () => {
  const { id } = useParams();
  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [suburbData, setSuburbData] = useState(null);
  const [suburbPropertyTypeData, setSuburbPropertyTypeData] = useState(null);
  const [suburbMonthlyData, setSuburbMonthlyData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showIntro, setShowIntro] = useState(true);
  const [closingIntro, setClosingIntro] = useState(false);
  const [chartHover, setChartHover] = useState(null);
  const chartRef = useRef(null);
  const isScrollingRef = useRef(false);

  const overviewRef = useRef(null);
  const comparablesRef = useRef(null);
  const locationRef = useRef(null);
  const improvementsRef = useRef(null);

  const teamRef = useRef(null);

  const sectionRefs = useRef({ overview: overviewRef, comparables: comparablesRef, location: locationRef, improvements: improvementsRef, team: teamRef });

  const handleTabClick = useCallback((key) => {
    setActiveTab(key);
    const ref = sectionRefs.current[key];
    if (ref?.current) {
      isScrollingRef.current = true;
      const topBarHeight = (document.querySelector('[data-agency-bar]')?.offsetHeight || 0) + (document.querySelector('[data-tab-bar]')?.offsetHeight || 0);
      const y = ref.current.getBoundingClientRect().top + window.scrollY - topBarHeight - 8;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTimeout(() => { isScrollingRef.current = false; }, 800);
    }
  }, []);

  // Scroll listener to update active tab based on topmost visible section
  useEffect(() => {
    const st = appraisal?.section_toggles || {};
    const isVisible = (key) => {
      if (Object.keys(st).length === 0) return true;
      if (key === 'overview') return st.details !== false || st.valuation !== false;
      return st[key] !== false;
    };
    const tabKeys = ['overview', 'comparables', 'location', 'improvements', 'team'].filter(isVisible);
    const refs = sectionRefs.current;
    const onScroll = () => {
      if (isScrollingRef.current) return;
      const offset = (document.querySelector('[data-agency-bar]')?.offsetHeight || 0)
        + (document.querySelector('[data-tab-bar]')?.offsetHeight || 0) + 16;
      let active = tabKeys[0];
      for (const key of tabKeys) {
        const el = refs[key]?.current;
        if (el && el.getBoundingClientRect().top <= offset) {
          active = key;
        }
      }
      setActiveTab(active);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [appraisal, suburbData]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch appraisal
  useEffect(() => {
    const fetchAppraisal = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/get-appraisal`,
          { appraisal_id: id },
          { 'Content-Type': 'application/json' }
        );
        if (response.data?.appraisal) {
          const data = response.data.appraisal;
          data.features = parseJsonField(data.features);
          data.sale_comparables = parseJsonField(data.sale_comparables) || [];
          data.rental_comparables = parseJsonField(data.rental_comparables) || [];
          data.images = parseJsonField(data.images) || [];
          setAppraisal(data);
        } else {
          setError('Appraisal not found.');
        }
      } catch (err) {
        console.error('Error fetching appraisal:', err);
        setError('Unable to load appraisal.');
      }
      setLoading(false);
    };
    fetchAppraisal();
  }, [id]);

  // Meta tags
  useEffect(() => {
    if (!appraisal) return;
    const agentName = appraisal.agent?.agent_name || appraisal.agent?.first_name || '';
    const agencyName = appraisal.agent?.agency_name || '';
    setMetaTags({
      title: `Property Appraisal — ${appraisal.address}`,
      description: `Property appraisal for ${appraisal.address}${agentName ? ` by ${agentName}, ${agencyName}` : ''}`,
      keywords: `property appraisal, ${appraisal.address}, ${agencyName}`,
      url: appraisal.published_url || `https://homeprice.site/appraisal/${id}`,
      image: appraisal.images?.[0] || appraisal.agent?.agency_logo || '',
    });
  }, [appraisal, id]);

  // Fetch suburb data for Trends & Location
  useEffect(() => {
    if (!appraisal) return;
    const settings = parseJsonField(appraisal.listing_search_settings);
    const suburbId = settings?.suburbs?.[0]?.value || deriveSuburbId(appraisal.address);
    if (!suburbId) return;
    const fetchSuburbData = async () => {
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/suburb`,
          { suburb_ids: [suburbId], generate_llm: false },
          { 'Content-Type': 'application/json' }
        );
        if (response.data && response.data.length > 0) {
          setSuburbData(response.data[0]);
        }
      } catch (err) {
        console.error('Error fetching suburb data:', err);
      }
    };
    fetchSuburbData();
  }, [appraisal]);

  // Fetch suburb property type + monthly data for Suburb Profile tab
  useEffect(() => {
    if (!appraisal) return;
    const settings = parseJsonField(appraisal.listing_search_settings);
    const suburbId = settings?.suburbs?.[0]?.value || deriveSuburbId(appraisal.address);
    if (!suburbId) return;
    const fetchSuburbProfileData = async () => {
      try {
        const [typeRes, monthlyRes] = await Promise.all([
          handleTokenAndPostRequest(
            `${window.api_host}/suburb-property-type-last-month-summary`,
            { suburb: suburbId },
            { 'Content-Type': 'application/json' }
          ),
          handleTokenAndPostRequest(
            `${window.api_host}/suburb-monthly`,
            { suburb: suburbId },
            { 'Content-Type': 'application/json' }
          ),
        ]);
        const typeData = typeof typeRes.data === 'string' ? JSON.parse(typeRes.data) : typeRes.data;
        setSuburbPropertyTypeData(typeData);
        const monthlyData = typeof monthlyRes.data === 'string' ? JSON.parse(monthlyRes.data) : monthlyRes.data;
        setSuburbMonthlyData(monthlyData);
      } catch (err) {
        console.error('Error fetching suburb profile data:', err);
      }
    };
    fetchSuburbProfileData();
  }, [appraisal]);

  // ---------------------------------------------------------------------------
  // Loading / Error states
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !appraisal) {
    return (
      <Container style={{ maxWidth: 800, paddingTop: 80, textAlign: 'center' }}>
        <h3 style={{ color: '#666' }}>{error || 'Appraisal not found.'}</h3>
      </Container>
    );
  }

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const agent = appraisal.agent || {};
  const features = appraisal.features || {};
  const images = Array.isArray(appraisal.images) ? appraisal.images : [];

  const beds = appraisal.bedrooms ?? features.bedrooms;
  const baths = appraisal.bathrooms ?? features.bathrooms;
  const parking = appraisal.parking ?? features.parking;
  const landSize = appraisal.land_size ?? features.land_size ?? null;
  const propertyType = appraisal.property_type ?? features.property_type;
  const yearBuilt = appraisal.year_built || features.year_built || null;
  const condition = features.condition;
  const propertyFeatures = Array.isArray(features.property_features) ? features.property_features : [];
  const imageImprovements = features.image_improvements && typeof features.image_improvements === 'object'
    ? Object.entries(features.image_improvements) : [];

  // Agency branding from appraisal agent object
  const accentColor = agent.primary_colour || '#2563eb';
  const accentIsLight = isLightColor(accentColor);
  const textAccentColor = accentIsLight ? '#1a1a1a' : accentColor;

  // Hero / intro overlay: always needs a dark background for white text
  const heroAccent = accentIsLight ? darkenColor(accentColor, 0.65) : accentColor;

  // Top bar: detect if text should be dark on a light bar background
  const topBarTextColor = accentIsLight ? '#1a1a1a' : (agent.font_colour || '#ffffff');

  // CTA button: text must be readable on white background
  const ctaTextColor = accentIsLight ? '#1a1a1a' : accentColor;

  // Section visibility from saved toggles
  const st = appraisal.section_toggles || {};
  const isSectionVisible = (viewKey) => {
    if (Object.keys(st).length === 0) return true; // no toggles saved = show all
    if (viewKey === 'overview') return st.details !== false || st.valuation !== false;
    return st[viewKey] !== false;
  };
  const fontColor = topBarTextColor;

  const createdDate = appraisal.created_at
    ? new Date(Number(appraisal.created_at) * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  // Feature pills
  const featurePills = [];
  if (features.pool) featurePills.push({ icon: <FaSwimmingPool size={12} />, label: 'Pool' });
  if (features.granny_flat) featurePills.push({ icon: <FaBuilding size={12} />, label: 'Granny Flat' });
  if (features.renovated) featurePills.push({ icon: <FaTools size={12} />, label: 'Renovated' });
  if (features.views) featurePills.push({ icon: <FaEye size={12} />, label: 'Views' });

  const agentFirstName = (agent.name || agent.agent_name || '').split(' ')[0];

  return (
    <div style={{ backgroundColor: '#f5f3f0', minHeight: '100vh', paddingBottom: (agent.agency_logo || agent.phone || agent.email) ? (isMobile ? 96 : 88) : 0 }}>

      {/* ── Intro Overlay ── */}
      {showIntro && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: `linear-gradient(165deg, ${heroAccent} 0%, ${heroAccent}dd 40%, #1a1a1a 100%)`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: isMobile ? '40px 24px' : '60px 40px',
            animation: closingIntro ? 'fadeOut 0.5s ease forwards' : 'fadeIn 0.5s ease',
            overflow: 'auto',
          }}
        >
          {/* Agency logo */}
          {agent.agency_logo && (
            <img
              src={agent.agency_logo}
              alt={agent.agency_name || 'Agency'}
              style={{
                height: isMobile ? 32 : 42,
                objectFit: 'contain',
                marginBottom: isMobile ? 32 : 48,
                opacity: 0.9,
                animation: 'slideUp 0.6s ease 0.1s both',
              }}
            />
          )}

          {/* Main content card */}
          <div style={{
            maxWidth: 520,
            width: '100%',
            textAlign: 'center',
            animation: 'slideUp 0.7s ease 0.2s both',
          }}>
            <div style={{
              fontSize: isMobile ? '0.65rem' : '0.72rem',
              fontWeight: 600,
              color: 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.2em',
              marginBottom: isMobile ? 16 : 20,
            }}>
              Prepared exclusively for you
            </div>

            <h1 style={{
              fontSize: isMobile ? '1.8rem' : '2.6rem',
              fontWeight: 700,
              color: '#fff',
              lineHeight: 1.15,
              margin: '0 0 12px',
              letterSpacing: '-0.03em',
            }}>
              Your Property Appraisal
            </h1>

            {appraisal.address && (
              <div style={{
                fontSize: isMobile ? '0.95rem' : '1.1rem',
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 500,
                marginBottom: isMobile ? 28 : 36,
                letterSpacing: '-0.01em',
              }}>
                {appraisal.address}
              </div>
            )}

            <div style={{
              height: 1,
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              margin: '0 auto 28px',
              maxWidth: 200,
            }} />

            <p style={{
              fontSize: isMobile ? '0.88rem' : '0.95rem',
              color: 'rgba(255,255,255,0.7)',
              lineHeight: 1.8,
              margin: '0 auto 36px',
              maxWidth: 440,
            }}>
              {agentFirstName || 'Your agent'} has put together a comprehensive analysis of your property
              {createdDate ? ` on ${createdDate}` : ''}. Inside you'll find comparable sales evidence, suburb market trends, a detailed location analysis, improvement recommendations, and an estimated value range.
            </p>

            {/* Feature highlights */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: isMobile ? 8 : 10,
              marginBottom: isMobile ? 36 : 48,
              animation: 'slideUp 0.8s ease 0.35s both',
            }}>
              {[
                { icon: <FaChartLine size={11} />, label: 'Market Trends' },
                { icon: <FaHome size={11} />, label: 'Comparable Sales' },
                { icon: <FaMapMarkerAlt size={11} />, label: 'Location Insights' },
                { icon: <FaLightbulb size={11} />, label: 'Improvements' },
                { icon: <FaDollarSign size={11} />, label: 'Value Estimate' },
              ].map((item, i) => (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px',
                  borderRadius: 20,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  fontSize: '0.72rem',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.75)',
                  backdropFilter: 'blur(4px)',
                }}>
                  {item.icon} {item.label}
                </span>
              ))}
            </div>

            {/* CTA Button */}
            <div style={{ animation: 'slideUp 0.9s ease 0.5s both' }}>
              <button
                onClick={() => {
                  trackEvent('click_view_appraisal');
                  const a = appraisal.agent || {};
                  handleTokenAndPostRequest(
                    `${window.api_host}/save-settings`,
                    {
                      agent_slug: a.landing_page_slug || a.slug || a.agent_slug,
                      event_type: 'view_appraisal',
                      metadata: {
                        property_address: appraisal.address || '',
                        appraisal_id: id,
                      },
                    },
                    { 'Content-Type': 'application/json' }
                  ).catch(err => console.error('Error saving view_appraisal event:', err));
                  setClosingIntro(true);
                  setTimeout(() => setShowIntro(false), 480);
                }}
                style={{
                  backgroundColor: '#fff',
                  color: ctaTextColor,
                  border: 'none',
                  borderRadius: 16,
                  padding: isMobile ? '16px 44px' : '18px 56px',
                  fontSize: isMobile ? '0.92rem' : '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  letterSpacing: '-0.01em',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.2)'; }}
              >
                View Your Appraisal →
              </button>
            </div>
          </div>

          {/* Agent attribution */}
          <div style={{
            marginTop: isMobile ? 40 : 56,
            display: 'flex', alignItems: 'center', gap: 12,
            animation: 'slideUp 1s ease 0.65s both',
          }}>
            {agent.photo ? (
              <img src={agent.photo} alt={agent.name || agent.agent_name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }} />
            ) : (
              <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>
                {(agent.name || agent.agent_name || '?')[0]}
              </div>
            )}
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                {agent.name || agent.agent_name || 'Your Agent'}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
                {agent.agency_name || ''}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fadeOut { from { opacity: 1 } to { opacity: 0 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>

      {/* Agency Top Bar */}
      {(agent.agency_logo || agent.agency_name) && (
        <div data-agency-bar style={{
          backgroundColor: accentColor,
          padding: isMobile ? '10px 20px' : '12px 40px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {agent.agency_logo && (
              <img src={agent.agency_logo} alt={agent.agency_name || 'Agency'} style={{ height: isMobile ? 28 : 36, objectFit: 'contain' }} />
            )}
            {!agent.agency_logo && agent.agency_name && (
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: fontColor, letterSpacing: '-0.01em' }}>
                {agent.agency_name}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.68rem', color: fontColor, opacity: 0.75, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Property Appraisal
          </div>
        </div>
      )}

      {/* Sticky Tab Bar */}
      <div data-tab-bar style={{
        position: 'sticky',
        top: (agent.agency_logo || agent.agency_name) ? (isMobile ? 48 : 60) : 0,
        zIndex: 99,
        backgroundColor: '#fff',
        borderBottom: '1px solid #e5e2dd',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <div style={{
          maxWidth: 900, margin: '0 auto',
          display: 'flex', gap: 0,
          justifyContent: 'center',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          {(() => {
            const st = appraisal.section_toggles || {};
            const allTabs = [
              { key: 'overview', label: 'Overview' },
              { key: 'comparables', label: 'Comparables' },
              { key: 'location', label: 'Location' },
              { key: 'improvements', label: 'Improvements' },
              { key: 'team', label: 'Team' },
            ];
            // Map view tabs to creation toggle keys; overview visible if details OR valuation is on
            const isVisible = (key) => {
              if (Object.keys(st).length === 0) return true; // no toggles saved = show all
              if (key === 'overview') return st.details !== false || st.valuation !== false;
              return st[key] !== false;
            };
            return allTabs.filter(t => isVisible(t.key)).map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              style={{
                flex: isMobile ? 1 : 'none',
                padding: isMobile ? '14px 8px' : '16px 24px',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: isMobile ? '0.68rem' : '0.78rem', fontWeight: 600,
                color: activeTab === tab.key ? textAccentColor : '#9ca3af',
                borderBottom: activeTab === tab.key ? `2px solid ${textAccentColor}` : '2px solid transparent',
                transition: 'color 0.2s, border-color 0.2s',
                whiteSpace: 'nowrap',
                letterSpacing: '0.02em',
              }}
            >
              {tab.label}
            </button>
            ));
          })()}
        </div>
      </div>

      {/* Hero Section — Full-bleed image with overlay */}
      {images.length > 0 ? (
        <div style={{ position: 'relative', width: '100%', height: isMobile ? 360 : 520, overflow: 'hidden' }}>
          <Carousel indicators={images.length > 1} controls={images.length > 1} interval={5000} fade>
            {images.map((url, idx) => (
              <Carousel.Item key={idx}>
                <img
                  src={url}
                  alt={`Property ${idx + 1}`}
                  style={{ width: '100%', height: isMobile ? 360 : 520, objectFit: 'cover', display: 'block' }}
                />
              </Carousel.Item>
            ))}
          </Carousel>
          {/* Gradient overlay */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
            pointerEvents: 'none', zIndex: 2,
          }} />
          {/* Agent badge on hero */}
          {(agent.photo || agent.agent_name || agent.first_name) && (
            <div style={{
              position: 'absolute', top: isMobile ? 16 : 24, right: isMobile ? 16 : 32,
              display: 'flex', alignItems: 'center', gap: 10,
              backgroundColor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)',
              padding: '8px 16px 8px 10px', borderRadius: 28,
              boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
              zIndex: 4,
            }}>
              {agent.photo && (
                <img src={agent.photo} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${accentColor}` }} />
              )}
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#1a1a1a', lineHeight: 1.2 }}>
                  {agent.agent_name || agent.first_name}
                </div>
                {agent.agency_name && (
                  <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: 1.2 }}>{agent.agency_name}</div>
                )}
              </div>
            </div>
          )}
          {/* Address on hero */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: isMobile ? '20px 24px' : '32px 48px',
            maxWidth: 900, margin: '0 auto', zIndex: 3,
          }}>
            <h1 style={{
              fontWeight: 700, fontSize: isMobile ? '1.5rem' : '2.2rem', color: '#fff',
              margin: 0, letterSpacing: '-0.02em', lineHeight: 1.2,
              textShadow: '0 2px 12px rgba(0,0,0,0.3)',
            }}>
              {appraisal.address}
            </h1>
            {createdDate && (
              <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', marginTop: 8 }}>
                Prepared {createdDate}{(agent.agent_name || agent.first_name) ? ` by ${agent.agent_name || agent.first_name}` : ''}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ padding: isMobile ? '40px 24px 20px' : '60px 48px 20px', maxWidth: 900, margin: '0 auto' }}>
          <h1 style={{ fontWeight: 700, fontSize: isMobile ? '1.5rem' : '2.2rem', color: '#1a1a1a', margin: 0, letterSpacing: '-0.02em' }}>
            {appraisal.address}
          </h1>
          {createdDate && (
            <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: 8 }}>
              Prepared {createdDate}
            </div>
          )}
        </div>
      )}

      <Container style={{ maxWidth: 900, paddingTop: images.length > 0 ? 32 : 12, paddingBottom: 80 }}>

        {/* ═══ OVERVIEW ═══ */}
        {isSectionVisible('overview') && (
        <div ref={overviewRef} data-section="overview">
        {/* Property Stats Bar */}
        {(beds != null || baths != null || parking != null || landSize != null || yearBuilt != null) && (
        <div style={{
          display: 'flex',
          gap: 0,
          marginBottom: 32,
          backgroundColor: '#fff',
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          ...(images.length > 0 ? { marginTop: -28, position: 'relative', zIndex: 2 } : {}),
        }}>
          {[
            beds != null && { icon: <FaBed size={18} />, value: beds, label: 'Beds' },
            baths != null && { icon: <FaBath size={18} />, value: baths, label: 'Baths' },
            parking != null && { icon: <FaCar size={18} />, value: parking, label: 'Parking' },
            landSize != null && { icon: <FaRulerCombined size={16} />, value: typeof landSize === 'object' ? `${landSize.size}${landSize.unit}` : typeof landSize === 'number' ? `${landSize}m²` : landSize, label: 'Land' },
            yearBuilt != null && { icon: <FaBuilding size={16} />, value: yearBuilt, label: 'Built' },
          ].filter(Boolean).map((stat, i, arr) => (
            <div key={i} style={{
              flex: 1, textAlign: 'center',
              padding: isMobile ? '18px 8px' : '24px 16px',
              borderRight: i < arr.length - 1 ? '1px solid #f0eeeb' : 'none',
            }}>
              <div style={{ color: accentColor, marginBottom: 6 }}>{stat.icon}</div>
              <div style={{ fontWeight: 700, fontSize: isMobile ? '1.05rem' : '1.2rem', color: '#1a1a1a' }}>{stat.value}</div>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2, fontWeight: 500 }}>{stat.label}</div>
            </div>
          ))}
        </div>
        )}

        {/* Property Type & Feature Pills */}
        {(propertyType || condition || featurePills.length > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 32 }}>
            {propertyType && (
              <span style={{
                backgroundColor: '#1a1a1a', color: '#fff',
                fontSize: '0.78rem', fontWeight: 600, padding: '6px 16px', borderRadius: 24,
                display: 'inline-flex', alignItems: 'center', gap: 5, letterSpacing: '0.02em',
              }}>
                <FaHome size={11} /> {propertyType}
              </span>
            )}
            {condition && (
              <span style={{
                backgroundColor: '#e8e6e1', color: '#4a4540',
                fontSize: '0.78rem', fontWeight: 600, padding: '6px 16px', borderRadius: 24,
              }}>
                {condition}
              </span>
            )}
            {featurePills.map((f, i) => (
              <span key={i} style={{
                backgroundColor: '#e8e6e1', color: '#4a4540',
                fontSize: '0.78rem', fontWeight: 500, padding: '6px 16px', borderRadius: 24,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>
                {f.icon} {f.label}
              </span>
            ))}
          </div>
        )}

        {/* Property Features */}
        {propertyFeatures.length > 0 && (
          <div style={{
            marginBottom: 32, borderRadius: 20,
            backgroundColor: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            padding: isMobile ? '28px 24px' : '40px 48px',
            borderTop: `3px solid ${accentColor}`,
          }}>
            <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 16, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
              Property Features
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {propertyFeatures.map((feature, i) => (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  backgroundColor: '#f5f3f0', color: '#4a4540',
                  fontSize: '0.82rem', fontWeight: 500, padding: '6px 14px', borderRadius: 20,
                }}>
                  <FaCheck size={10} style={{ color: accentColor }} /> {feature}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Sale Estimate */}
        {appraisal.include_sale && (appraisal.price_low != null || appraisal.price_high != null) && (
          <div style={{
            marginBottom: 32, borderRadius: 20, overflow: 'hidden',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}>
            <div style={{ padding: isMobile ? '28px 24px' : '40px 48px' }}>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 8, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
                Sale Price Estimate
              </div>
              <div style={{
                fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: 800, color: '#fff',
                letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 4,
              }}>
                {fmt(appraisal.price_low)} — {fmt(appraisal.price_high)}
              </div>
              {appraisal.price_mid != null && (
                <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.6)', marginTop: 8, fontWeight: 400 }}>
                  Mid-point estimate: <strong style={{ color: '#fff' }}>{fmt(appraisal.price_mid)}</strong>
                </div>
              )}
              {/* Range bar */}
              <div style={{ margin: '28px 0 0', maxWidth: 420 }}>
                <div style={{ position: 'relative', height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.12)' }}>
                  <div style={{
                    position: 'absolute', left: '15%', right: '15%', top: 0, bottom: 0,
                    borderRadius: 3, backgroundColor: accentColor, opacity: 0.6,
                  }} />
                  <div style={{
                    position: 'absolute', left: '50%', top: -5, transform: 'translateX(-50%)',
                    width: 16, height: 16, borderRadius: '50%',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                  <span>{fmt(appraisal.price_low)}</span>
                  <span>{fmt(appraisal.price_high)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rental Estimate */}
        {appraisal.include_rental && (appraisal.rent_low != null || appraisal.rent_high != null) && (
          <div style={{
            marginBottom: 32, borderRadius: 20, overflow: 'hidden',
            backgroundColor: '#fff',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            borderTop: `3px solid ${accentColor}`,
          }}>
            <div style={{ padding: isMobile ? '28px 24px' : '40px 48px' }}>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 8, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
                Rental Estimate
              </div>
              <div style={{
                fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: 800, color: '#1a1a1a',
                letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 4,
              }}>
                {fmtWeekly(appraisal.rent_low)} — {fmtWeekly(appraisal.rent_high)}
              </div>
              {appraisal.rent_mid != null && (
                <div style={{ fontSize: '1rem', color: '#9ca3af', marginTop: 8, fontWeight: 400 }}>
                  Mid-point estimate: <strong style={{ color: '#1a1a1a' }}>{fmtWeekly(appraisal.rent_mid)}</strong>
                </div>
              )}
              <div style={{ margin: '28px 0 0', maxWidth: 420 }}>
                <div style={{ position: 'relative', height: 6, borderRadius: 3, backgroundColor: '#f0eeeb' }}>
                  <div style={{
                    position: 'absolute', left: '15%', right: '15%', top: 0, bottom: 0,
                    borderRadius: 3, backgroundColor: accentColor, opacity: 0.25,
                  }} />
                  <div style={{
                    position: 'absolute', left: '50%', top: -5, transform: 'translateX(-50%)',
                    width: 16, height: 16, borderRadius: '50%',
                    backgroundColor: accentColor,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: '0.75rem', color: '#bbb' }}>
                  <span>{fmtWeekly(appraisal.rent_low)}</span>
                  <span>{fmtWeekly(appraisal.rent_high)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Agent Notes */}
        {features.notes && (
          <div style={{
            marginBottom: 32, borderRadius: 20,
            backgroundColor: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            padding: isMobile ? '28px 24px' : '40px 48px',
            borderTop: `3px solid ${accentColor}`,
          }}>
            <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 16, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
              Agent Notes
            </div>
            <p style={{ color: '#374151', lineHeight: 1.8, fontSize: '0.95rem', marginBottom: 0, whiteSpace: 'pre-line' }}>
              {features.notes}
            </p>
          </div>
        )}
        </div>
        )}

        {/* ═══ COMPARABLES ═══ */}
        {isSectionVisible('comparables') && (
        <div ref={comparablesRef} data-section="comparables">
        {/* Sale Comparables */}
        {appraisal.include_sale && appraisal.sale_comparables.length > 0 && (
          <div style={{ marginBottom: 36 }}>
            <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 20, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
              Sale Comparables
            </div>
            <Row xs={1} md={2} className="g-3" style={{ padding: '0 4px' }}>
              {appraisal.sale_comparables.map((comp, ci) => (
                <Col key={comp.id || ci}>
                  <ComparableCard comp={comp} textAccentColor={textAccentColor} isMobile={isMobile} />
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Rental Comparables */}
        {appraisal.include_rental && appraisal.rental_comparables.length > 0 && (
          <div style={{ marginBottom: 36 }}>
            <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 20, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
              Rental Comparables
            </div>
            <Row xs={1} md={2} className="g-3" style={{ padding: '0 4px' }}>
              {appraisal.rental_comparables.map((comp, ci) => (
                <Col key={comp.id || ci}>
                  <ComparableCard comp={comp} textAccentColor={textAccentColor} isMobile={isMobile} />
                </Col>
              ))}
            </Row>
          </div>
        )}
        </div>
        )}

        {/* ═══ LOCATION ═══ */}
        {isSectionVisible('location') && (
        <div ref={locationRef} data-section="location">
        {/* Trends & Location */}
        {suburbData && (
          <TrendsLocationSection suburbData={suburbData} accentColor={accentColor} textAccentColor={textAccentColor} isMobile={isMobile} />
        )}

        {/* ═══ LOCATION (continued) — Location Analysis & Map ═══ */}
        {/* Location Analysis */}
        {features.location_analysis && (() => {
          const la = features.location_analysis;
          const amenityIcon = (type) => {
            switch (type) {
              case 'school': return <FaSchool size={12} />;
              case 'shopping': return <FaShoppingCart size={12} />;
              case 'park': return <FaTree size={12} />;
              default: return <FaMapMarkerAlt size={12} />;
            }
          };
          const impactStyles = {
            positive: { color: '#16a34a', bg: '#f0fdf4', icon: <FaArrowUp size={9} /> },
            negative: { color: '#dc2626', bg: '#fef2f2', icon: <FaArrowDown size={9} /> },
            neutral: { color: '#6b7280', bg: '#f3f4f6', icon: <FaMinus size={9} /> },
          };
          const advantageStyles = {
            better: { color: '#dc2626', label: 'Comparable has better location' },
            worse: { color: '#16a34a', label: 'Your property has better location' },
            similar: { color: '#6b7280', label: 'Similar location' },
          };
          return (
            <div style={{ marginBottom: 36 }}>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 20, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
                Location Analysis
              </div>
              <div style={{
                borderRadius: 20, overflow: 'hidden', backgroundColor: '#fff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                borderTop: `3px solid ${accentColor}`,
              }}>
                {/* Summary + street info header */}
                <div style={{ padding: isMobile ? '24px 20px' : '32px 32px 28px' }}>
                  {la.summary && (
                    <p style={{ fontSize: '0.95rem', color: '#374151', lineHeight: 1.75, margin: '0 0 20px' }}>
                      {la.summary}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {la.street_type && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: '#f5f3f0', padding: '6px 14px', borderRadius: 20,
                        fontSize: '0.78rem', fontWeight: 600, color: '#1a1a1a',
                      }}>
                        <FaRoute size={11} style={{ color: accentColor }} /> {la.street_type}
                      </span>
                    )}
                    {la.neighbourhood_type && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: '#f5f3f0', padding: '6px 14px', borderRadius: 20,
                        fontSize: '0.78rem', fontWeight: 600, color: '#1a1a1a',
                      }}>
                        <FaHome size={11} style={{ color: accentColor }} /> {la.neighbourhood_type}
                      </span>
                    )}
                  </div>
                </div>

                {/* Positives & Negatives */}
                {(la.positives?.length > 0 || la.negatives?.length > 0) && (
                  <div style={{
                    borderTop: '1px solid #f0eeeb',
                    display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  }}>
                    {la.positives?.length > 0 && (
                      <div style={{ padding: isMobile ? '20px 20px' : '24px 32px', borderRight: isMobile ? 'none' : '1px solid #f0eeeb' }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, color: '#16a34a', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaThumbsUp size={11} /> Positives
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {la.positives.map((p, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.85rem', color: '#374151', lineHeight: 1.5 }}>
                              <FaCheck size={10} style={{ color: '#16a34a', marginTop: 4, flexShrink: 0 }} />
                              {p}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {la.negatives?.length > 0 && (
                      <div style={{ padding: isMobile ? '20px 20px' : '24px 32px', borderTop: isMobile ? '1px solid #f0eeeb' : 'none' }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, color: '#dc2626', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaExclamationTriangle size={11} /> Considerations
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {la.negatives.map((n, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.85rem', color: '#374151', lineHeight: 1.5 }}>
                              <FaMinus size={9} style={{ color: '#dc2626', marginTop: 5, flexShrink: 0 }} />
                              {n}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Nearby Amenities */}
                {la.nearby_amenities?.length > 0 && (
                  <div style={{ borderTop: '1px solid #f0eeeb', padding: isMobile ? '20px 20px' : '24px 32px' }}>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, color: '#9ca3af', marginBottom: 16 }}>
                      Nearby Amenities
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                      {la.nearby_amenities.map((a, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          backgroundColor: '#f9fafb', borderRadius: 14, padding: '12px 16px',
                        }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: '50%',
                            backgroundColor: '#f0eeeb',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: textAccentColor, flexShrink: 0,
                          }}>
                            {amenityIcon(a.type)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>
                              {a.name}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>
                              {a.distance_label}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Value Factors */}
                {la.value_factors?.length > 0 && (
                  <div style={{ borderTop: '1px solid #f0eeeb', padding: isMobile ? '20px 20px' : '24px 32px' }}>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, color: '#9ca3af', marginBottom: 16 }}>
                      Value Factors
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {la.value_factors.map((vf, i) => {
                        const is = impactStyles[vf.impact] || impactStyles.neutral;
                        return (
                          <div key={i} style={{
                            display: 'flex', gap: 14, alignItems: 'flex-start',
                            backgroundColor: is.bg, borderRadius: 14, padding: '14px 18px',
                          }}>
                            <div style={{
                              width: 26, height: 26, borderRadius: '50%',
                              backgroundColor: '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0, marginTop: 1, color: is.color,
                            }}>
                              {is.icon}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1a1a1a', marginBottom: 3 }}>
                                {vf.factor}
                              </div>
                              <div style={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.6 }}>
                                {vf.detail}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            </div>
          );
        })()}

        {/* Property Map */}
        {(() => {
          const allComps = [...(appraisal.sale_comparables || []), ...(appraisal.rental_comparables || [])];
          const mappable = allComps.filter(c => c.latitude && c.longitude);
          if (mappable.length === 0) return null;
          const mapData = mappable.map(c => ({
            ...c,
            filter_date_label: c.listing_type === 'Rent' ? 'Rented' : 'Sold',
          }));
          const subjectProperty = {
            latitude: features.latitude || appraisal.latitude,
            longitude: features.longitude || appraisal.longitude,
            display_address: appraisal.address,
            photo_url_main: images[0] || null,
            bedrooms: beds,
            bathrooms: baths,
            carspaces: parking,
            land_area: landSize,
            property_type_display: propertyType,
          };
          return (
            <div style={{ marginBottom: 36 }}>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 20, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
                Property Map
              </div>
              <div style={{
                borderRadius: 20, overflow: 'hidden',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              }}>
                <AppraisalMap
                  comparables={mapData}
                  subjectProperty={subjectProperty}
                  accentColor={accentColor}
                />
              </div>
            </div>
          );
        })()}

        {/* Suburb Profile — under Location */}
        {suburbData && (() => {
          let ratings = null;
          let ratingExplanations = null;
          let upsides = [];
          let downsides = [];
          try { ratings = suburbData.ratings ? JSON.parse(suburbData.ratings) : null; } catch (e) { ratings = null; }
          try { ratingExplanations = suburbData.rating_explanations ? JSON.parse(suburbData.rating_explanations) : null; } catch (e) { ratingExplanations = null; }
          try { upsides = suburbData.upsides ? JSON.parse(suburbData.upsides) : []; } catch (e) { upsides = []; }
          try { downsides = suburbData.downsides ? JSON.parse(suburbData.downsides) : []; } catch (e) { downsides = []; }

          const monthlyPoints = suburbMonthlyData?.data || [];

          const priceHistory = [];
          if (monthlyPoints.length > 0) {
            const grouped = {};
            monthlyPoints.forEach(d => {
              const m = d.sold_month;
              if (!grouped[m]) grouped[m] = [];
              if (d.median_sold_price_filled != null) grouped[m].push(d.median_sold_price_filled);
            });
            Object.keys(grouped).sort().forEach(m => {
              const avg = grouped[m].reduce((a, b) => a + b, 0) / grouped[m].length;
              priceHistory.push({ month: m, price: avg });
            });
          }

          return (
            <div style={{ marginBottom: 36 }}>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 20, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
                Suburb Profile
              </div>

              {/* Suburb header card */}
              <div style={{
                borderRadius: 20, overflow: 'hidden', backgroundColor: '#fff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                borderTop: `3px solid ${accentColor}`,
                marginBottom: 20,
              }}>
                <div style={{ padding: isMobile ? '24px 20px' : '32px 32px 28px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <FaMapMarkerAlt size={14} style={{ color: accentColor }} />
                    <span style={{ fontWeight: 700, fontSize: '1.2rem', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                      {suburbData.suburb_initcap || 'Suburb'}
                    </span>
                    {(suburbData.state || suburbData.postcode) && (
                      <span style={{ fontSize: '0.85rem', color: '#9ca3af', fontWeight: 400 }}>
                        {[suburbData.state, suburbData.postcode].filter(Boolean).join(' ')}
                      </span>
                    )}
                  </div>
                  {suburbData.suburb_summary && (
                    <p style={{ fontSize: '0.92rem', color: '#4b5563', lineHeight: 1.75, margin: 0 }}>
                      {suburbData.suburb_summary}
                    </p>
                  )}
                </div>

                {/* Ratings grid */}
                {ratings && (
                  <div style={{ borderTop: '1px solid #f0eeeb', padding: isMobile ? '20px 20px' : '24px 32px' }}>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, color: '#9ca3af', marginBottom: 16 }}>
                      Suburb Ratings
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 14 }}>
                      {Object.entries(ratings).map(([category, score]) => {
                        const s = Math.max(0, Math.min(5, Math.round(Number(score))));
                        const explanation = ratingExplanations?.[category];
                        return (
                          <div key={category} style={{
                            backgroundColor: '#f9fafb', borderRadius: 14, padding: '14px 16px',
                          }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'capitalize', marginBottom: 8, letterSpacing: '0.02em' }}>
                              {category.replace(/_/g, ' ')}
                            </div>
                            <div style={{ display: 'flex', gap: 3, marginBottom: explanation ? 6 : 0 }}>
                              {[1, 2, 3, 4, 5].map(i => (
                                <FaStar key={i} size={12} style={{ color: i <= s ? accentColor : '#e5e7eb' }} />
                              ))}
                            </div>
                            {explanation && (
                              <div style={{ fontSize: '0.72rem', color: '#9ca3af', lineHeight: 1.5 }}>
                                {explanation}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Upsides & Downsides */}
                {(upsides.length > 0 || downsides.length > 0) && (
                  <div style={{
                    borderTop: '1px solid #f0eeeb',
                    display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  }}>
                    {upsides.length > 0 && (
                      <div style={{ padding: isMobile ? '20px 20px' : '24px 32px', borderRight: isMobile ? 'none' : '1px solid #f0eeeb' }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, color: '#16a34a', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaThumbsUp size={11} /> Upsides
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {upsides.map((u, i) => {
                            const [title, ...rest] = u.split(':');
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.85rem', color: '#374151', lineHeight: 1.5 }}>
                                <FaCheck size={10} style={{ color: '#16a34a', marginTop: 4, flexShrink: 0 }} />
                                <span><strong>{title}</strong>{rest.length > 0 ? ':' + rest.join(':') : ''}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {downsides.length > 0 && (
                      <div style={{ padding: isMobile ? '20px 20px' : '24px 32px', borderTop: isMobile ? '1px solid #f0eeeb' : 'none' }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, color: '#dc2626', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaExclamationTriangle size={11} /> Considerations
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {downsides.map((d, i) => {
                            const [title, ...rest] = d.split(':');
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.85rem', color: '#374151', lineHeight: 1.5 }}>
                                <FaMinus size={9} style={{ color: '#dc2626', marginTop: 5, flexShrink: 0 }} />
                                <span><strong>{title}</strong>{rest.length > 0 ? ':' + rest.join(':') : ''}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Price Trend Chart */}
              {priceHistory.length > 1 && (
                <div style={{
                  borderRadius: 20, overflow: 'hidden', backgroundColor: '#fff',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ padding: isMobile ? '20px 20px' : '28px 32px' }}>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, color: '#9ca3af', marginBottom: 20 }}>
                      Median Sale Price Trend
                    </div>
                    {(() => {
                      const prices = priceHistory.map(p => p.price);
                      const minP = Math.min(...prices);
                      const maxP = Math.max(...prices);
                      const range = maxP - minP || 1;
                      const chartH = isMobile ? 120 : 160;
                      const padLeft = 60;
                      const padRight = 12;
                      const padTop = 10;
                      const padBot = 10;

                      /* ── Y-axis ticks ── */
                      const tickCount = 4;
                      const yTicks = Array.from({ length: tickCount }, (_, i) => {
                        const v = minP + (range * i) / (tickCount - 1);
                        return v;
                      });
                      const fmtTick = (v) => {
                        if (v >= 1e6) return `$${(v / 1e6).toFixed(v % 1e6 === 0 ? 0 : 1)}M`;
                        if (v >= 1e3) return `$${Math.round(v / 1e3)}K`;
                        return `$${Math.round(v)}`;
                      };

                      /* ── data point positions ── */
                      const dataPoints = priceHistory.map((p, i) => {
                        const x = padLeft + (i / (priceHistory.length - 1)) * (100 - padLeft - padRight);
                        const y = chartH - padBot - ((p.price - minP) / range) * (chartH - padTop - padBot);
                        return { x, y, price: p.price, month: p.month };
                      });
                      const points = dataPoints.map(d => `${d.x},${d.y}`).join(' ');
                      const areaPoints = points + ` ${dataPoints[dataPoints.length - 1].x},${chartH - padBot} ${dataPoints[0].x},${chartH - padBot}`;

                      const firstP = priceHistory[0];
                      const lastP = priceHistory[priceHistory.length - 1];
                      const priceDelta = lastP.price - firstP.price;
                      const pricePctChange = firstP.price > 0 ? ((priceDelta / firstP.price) * 100).toFixed(1) : null;

                      const handleChartMouseMove = (e) => {
                        const rect = chartRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        const xPx = e.clientX - rect.left;
                        const xPct = (xPx / rect.width) * 100;
                        let closest = 0;
                        let closestDist = Infinity;
                        dataPoints.forEach((d, i) => {
                          const dist = Math.abs(d.x - xPct);
                          if (dist < closestDist) { closestDist = dist; closest = i; }
                        });
                        setChartHover(closest);
                      };

                      const hp = chartHover != null ? dataPoints[chartHover] : null;

                      return (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
                            <div>
                              <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1a1a1a' }}>
                                ${Math.round(lastP.price).toLocaleString('en-AU')}
                              </span>
                              <span style={{ fontSize: '0.78rem', color: '#9ca3af', marginLeft: 8 }}>latest median</span>
                            </div>
                            {pricePctChange != null && (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: '0.78rem', fontWeight: 600,
                                color: priceDelta >= 0 ? '#16a34a' : '#dc2626',
                              }}>
                                {priceDelta >= 0 ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                                {priceDelta >= 0 ? '+' : ''}{pricePctChange}%
                              </span>
                            )}
                          </div>
                          <div
                            ref={chartRef}
                            style={{ position: 'relative', cursor: 'crosshair' }}
                            onMouseMove={handleChartMouseMove}
                            onMouseLeave={() => setChartHover(null)}
                            onTouchMove={(e) => { const t = e.touches[0]; handleChartMouseMove(t); }}
                            onTouchEnd={() => setChartHover(null)}
                          >
                            <svg viewBox={`0 0 100 ${chartH}`} preserveAspectRatio="none" style={{ width: '100%', height: chartH, display: 'block' }}>
                              <defs>
                                <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={textAccentColor} stopOpacity="0.15" />
                                  <stop offset="100%" stopColor={textAccentColor} stopOpacity="0.01" />
                                </linearGradient>
                              </defs>
                              {/* Y-axis grid lines + labels */}
                              {yTicks.map((v, i) => {
                                const y = chartH - padBot - ((v - minP) / range) * (chartH - padTop - padBot);
                                return (
                                  <g key={i}>
                                    <line x1={padLeft} y1={y} x2={100 - padRight} y2={y} stroke="#e5e7eb" strokeWidth="0.25" vectorEffect="non-scaling-stroke" />
                                    <text x={padLeft - 2} y={y + 1} textAnchor="end" fill="#9ca3af" style={{ fontSize: '3.5px' }}>{fmtTick(v)}</text>
                                  </g>
                                );
                              })}
                              <polygon points={areaPoints} fill="url(#priceFill)" />
                              <polyline points={points} fill="none" stroke={textAccentColor} strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
                              {/* hover vertical line + dot */}
                              {hp && (
                                <>
                                  <line x1={hp.x} y1={padTop} x2={hp.x} y2={chartH - padBot} stroke="#9ca3af" strokeWidth="0.3" strokeDasharray="1.5,1.5" vectorEffect="non-scaling-stroke" />
                                  <circle cx={hp.x} cy={hp.y} r="1.2" fill={textAccentColor} stroke="#fff" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
                                </>
                              )}
                            </svg>
                            {/* hover tooltip */}
                            {hp && (
                              <div style={{
                                position: 'absolute',
                                left: `${hp.x}%`, top: 0,
                                transform: hp.x > 70 ? 'translateX(-110%)' : 'translateX(10%)',
                                background: '#1a1a1a', color: '#fff', borderRadius: 8,
                                padding: '6px 10px', fontSize: '0.72rem', whiteSpace: 'nowrap',
                                pointerEvents: 'none', zIndex: 5,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                              }}>
                                <div style={{ fontWeight: 600 }}>${Math.round(hp.price).toLocaleString('en-AU')}</div>
                                <div style={{ color: '#9ca3af', fontSize: '0.65rem' }}>{hp.month}</div>
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingLeft: padLeft + '%', paddingRight: padRight + '%' }}>
                            <span style={{ fontSize: '0.68rem', color: '#b8b5af' }}>{firstP.month}</span>
                            <span style={{ fontSize: '0.68rem', color: '#b8b5af' }}>{lastP.month}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        </div>
        )}

        {/* ═══ IMPROVEMENTS ═══ */}
        {isSectionVisible('improvements') && (
        <div ref={improvementsRef} data-section="improvements">
        {/* Image Improvements */}
        {imageImprovements.length > 0 && (
          <div style={{ marginBottom: 36 }}>
            <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 20, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
              Presentation Improvements
            </div>
            {imageImprovements.map(([imageUrl, data], idx) => (
              <ImageImprovementCard
                key={idx}
                imageUrl={imageUrl}
                data={data}
                index={idx}
                accentColor={accentColor}
                textAccentColor={textAccentColor}
                isMobile={isMobile}
              />
            ))}
          </div>
        )}
        </div>
        )}

        {/* ═══ TEAM ═══ */}
        {isSectionVisible('team') && (
        <div ref={teamRef} data-section="team">
        {/* ── Agency & Team Section (premium) ── */}
        {appraisal.agency_stats && (() => {
          const stats = appraisal.agency_stats;
          const team = Array.isArray(stats.team) ? stats.team : [];
          const recentListings = Array.isArray(stats.selected_recent_listings) ? stats.selected_recent_listings
            : Array.isArray(stats.recent_listings) ? stats.recent_listings : [];
          return (
            <div style={{ marginBottom: 36 }}>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 20, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
                Your Team
              </div>

              {/* Agency overview card */}
              <div style={{
                borderRadius: 20, overflow: 'hidden',
                backgroundColor: '#fff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                marginBottom: 20,
              }}>
                <div style={{ height: 5, background: `linear-gradient(90deg, ${accentColor}, ${accentColor}88)` }} />
                <div style={{ padding: isMobile ? '24px 20px' : '32px 36px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                    {agent.agency_logo && (
                      <div style={{ backgroundColor: accentColor, borderRadius: 14, padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <img src={agent.agency_logo} alt={stats.agency_name || agent.agency_name} style={{ height: 30, objectFit: 'contain' }} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                        {stats.agency_name || stats.name || agent.agency_name}
                      </div>
                    </div>
                  </div>

                  {/* Agency stats grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: 12,
                    marginBottom: (Array.isArray(stats.sales_by_type) && stats.sales_by_type.length > 0) ? 16 : 0,
                  }}>
                    {(stats.total_listings || stats.properties_sold) && (
                      <div style={{ backgroundColor: '#f9fafb', borderRadius: 14, padding: '16px 18px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: textAccentColor, letterSpacing: '-0.02em' }}>{stats.total_listings || stats.properties_sold}</div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>Properties Sold</div>
                      </div>
                    )}
                    {stats.avg_sale_price && (
                      <div style={{ backgroundColor: '#f9fafb', borderRadius: 14, padding: '16px 18px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: textAccentColor, letterSpacing: '-0.02em' }}>{stats.avg_sale_price}</div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>Avg Sale Price</div>
                      </div>
                    )}
                    {stats.total_sales_value && (
                      <div style={{ backgroundColor: '#f9fafb', borderRadius: 14, padding: '16px 18px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: textAccentColor, letterSpacing: '-0.02em' }}>{stats.total_sales_value}</div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>Total Sales Value</div>
                      </div>
                    )}
                    {stats.avg_days_on_market && (
                      <div style={{ backgroundColor: '#f9fafb', borderRadius: 14, padding: '16px 18px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: textAccentColor, letterSpacing: '-0.02em' }}>{stats.avg_days_on_market}</div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>Avg Days on Market</div>
                      </div>
                    )}
                  </div>

                  {/* Agency sales by type pills */}
                  {Array.isArray(stats.sales_by_type) && stats.sales_by_type.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {stats.sales_by_type.map((s, si) => (
                        <span key={si} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          backgroundColor: `${accentColor}08`, border: `1px solid ${accentColor}18`,
                          borderRadius: 20, padding: '5px 14px',
                          fontSize: '0.75rem', fontWeight: 500, color: '#4b5563',
                        }}>
                          <span style={{ fontWeight: 700, color: textAccentColor }}>{s.count}</span> {s.type}
                          {s.avg_price && <span style={{ color: '#9ca3af', fontWeight: 400 }}>· {s.avg_price}</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Team member cards */}
              {team.map((member, idx) => (
                <div key={member.agent_email || idx} style={{
                  borderRadius: 20, overflow: 'hidden',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  marginBottom: idx < team.length - 1 ? 16 : 0,
                }}>
                  <div style={{
                    padding: isMobile ? '24px 20px' : '28px 36px',
                    display: 'flex', gap: isMobile ? 16 : 24, alignItems: 'flex-start',
                    flexDirection: isMobile ? 'column' : 'row',
                  }}>
                    {/* Agent Photo & Name */}
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>
                      {member.photo_url ? (
                        <img src={member.photo_url} alt={member.name} style={{
                          width: 64, height: 64, borderRadius: '50%', objectFit: 'cover',
                          border: `3px solid ${accentColor}20`, flexShrink: 0,
                        }} />
                      ) : (
                        <div style={{
                          width: 64, height: 64, borderRadius: '50%',
                          background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}08)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, color: textAccentColor, fontSize: '1.3rem', fontWeight: 700,
                        }}>
                          {(member.name || '?').charAt(0)}
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                          {member.name}
                        </div>
                        {member.sold != null && (
                          <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 2 }}>
                            {member.sold} properties sold
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                      {/* Performance stats row */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                        gap: 10, marginBottom: (Array.isArray(member.sales_by_type) && member.sales_by_type.length > 0) ? 16 : 0,
                      }}>
                        {member.avg_sale_price && (
                          <div style={{ backgroundColor: '#f9fafb', borderRadius: 12, padding: '12px 14px' }}>
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: textAccentColor }}>{member.avg_sale_price}</div>
                            <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>Avg Sale</div>
                          </div>
                        )}
                        {member.total_sales_value && (
                          <div style={{ backgroundColor: '#f9fafb', borderRadius: 12, padding: '12px 14px' }}>
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: textAccentColor }}>{member.total_sales_value}</div>
                            <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>Total Value</div>
                          </div>
                        )}
                        {member.avg_days_on_market && (
                          <div style={{ backgroundColor: '#f9fafb', borderRadius: 12, padding: '12px 14px' }}>
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: textAccentColor }}>{member.avg_days_on_market} <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#9ca3af' }}>days</span></div>
                            <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>Avg Days on Market</div>
                          </div>
                        )}
                      </div>

                      {/* Sales by type pills */}
                      {Array.isArray(member.sales_by_type) && member.sales_by_type.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {member.sales_by_type.map((s, si) => (
                            <span key={si} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              backgroundColor: `${accentColor}08`, border: `1px solid ${accentColor}18`,
                              borderRadius: 20, padding: '5px 14px',
                              fontSize: '0.75rem', fontWeight: 500, color: '#4b5563',
                            }}>
                              <span style={{ fontWeight: 700, color: textAccentColor }}>{s.count}</span> {s.type}
                              {s.avg_price && <span style={{ color: '#9ca3af', fontWeight: 400 }}>· {s.avg_price}</span>}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Recent Listings */}
              {recentListings.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 16, borderLeft: `3px solid ${accentColor}`, paddingLeft: 12 }}>
                    Recent Results
                  </div>
                  <Row xs={1} md={2} className="g-3" style={{ padding: '0 4px' }}>
                    {recentListings.map((listing, li) => (
                      <Col key={listing.id || li}>
                              <div style={{
                                borderRadius: 16, overflow: 'hidden',
                                backgroundColor: '#fff',
                                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                                marginBottom: 20,
                              }}>
                                {(() => {
                                  const imgs = (listing.images && listing.images.length > 0)
                                    ? listing.images
                                    : [listing.photo].filter(Boolean);
                                  if (imgs.length > 1) {
                                    return (
                                      <Carousel interval={null} wrap={false} indicators={false} controls>
                                        {imgs.map((url, idx) => (
                                          <Carousel.Item key={idx}>
                                            <img src={url} alt={`${listing.address} ${idx + 1}`} style={{
                                              width: '100%', height: 160, objectFit: 'cover',
                                            }} />
                                          </Carousel.Item>
                                        ))}
                                      </Carousel>
                                    );
                                  } else if (imgs.length === 1) {
                                    return (
                                      <img src={imgs[0]} alt={listing.address} style={{
                                        width: '100%', height: 160, objectFit: 'cover',
                                      }} />
                                    );
                                  }
                                  return null;
                                })()}
                                <div style={{ padding: '16px 20px' }}>
                                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a', marginBottom: 4 }}>
                                    {listing.display_price || listing.price}
                                  </div>
                                  <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <FaMapMarkerAlt size={10} style={{ color: '#9ca3af' }} /> {listing.address}
                                  </div>
                                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: '0.75rem', color: '#9ca3af', marginBottom: 8 }}>
                                    {listing.bedrooms != null && (
                                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FaBed size={11} /> {listing.bedrooms}</span>
                                    )}
                                    {listing.bathrooms != null && (
                                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FaBath size={11} /> {listing.bathrooms}</span>
                                    )}
                                    {listing.carspaces != null && (
                                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FaCar size={11} /> {listing.carspaces}</span>
                                    )}
                                    {listing.property_type && (
                                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FaHome size={11} /> {listing.property_type}</span>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', color: '#9ca3af' }}>
                                    {listing.sold_date && <span>{new Date(listing.sold_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                                    {listing.agent_names && <span>{Array.isArray(listing.agent_names) ? listing.agent_names.join(', ') : listing.agent_names}</span>}
                                  </div>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                </div>
              )}
            </div>
          );
        })()}

        {/* Agent Card — Premium footer */}
        {(agent.agent_name || agent.first_name) && !appraisal.agency_stats && (
          <div style={{
            marginBottom: 32, borderRadius: 20, overflow: 'hidden',
            backgroundColor: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}>
            <div style={{
              height: 6, background: accentColor,
            }} />
            <div style={{ padding: isMobile ? '24px' : '32px 40px', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              {agent.photo && (
                <img
                  src={agent.photo}
                  alt={agent.agent_name || agent.first_name}
                  style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '3px solid #f0eeeb' }}
                />
              )}
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                  {agent.agent_name || agent.first_name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: 8 }}>{agent.agency_name}</div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.85rem' }}>
                  {agent.phone && (
                    <a
                      href={`tel:${agent.phone}`}
                      onClick={() => {
                        trackEvent('click_agent_phone', { phone: agent.phone });
                        handleTokenAndPostRequest(
                          `${window.api_host}/save-settings`,
                          {
                            agent_slug: agent.landing_page_slug || agent.slug || agent.agent_slug,
                            event_type: 'click_agent_phone',
                            metadata: {
                              property_address: appraisal.address || '',
                              appraisal_id: id,
                              phone: agent.phone,
                            },
                          },
                          { 'Content-Type': 'application/json' }
                        ).catch(err => console.error('Error saving click_agent_phone event:', err));
                      }}
                      style={{ color: textAccentColor, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                      <FaPhone size={12} /> {formatPhoneNumber(agent.phone)}
                    </a>
                  )}
                  {agent.email && (
                    <a
                      href={`mailto:${agent.email}`}
                      onClick={() => {
                        trackEvent('click_agent_email', { email: agent.email });
                        handleTokenAndPostRequest(
                          `${window.api_host}/save-settings`,
                          {
                            agent_slug: agent.landing_page_slug || agent.slug || agent.agent_slug,
                            event_type: 'click_agent_email',
                            metadata: {
                              property_address: appraisal.address || '',
                              appraisal_id: id,
                              email: agent.email,
                            },
                          },
                          { 'Content-Type': 'application/json' }
                        ).catch(err => console.error('Error saving click_agent_email event:', err));
                      }}
                      style={{ color: textAccentColor, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                      <FaEnvelope size={12} /> {agent.email}
                    </a>
                  )}
                </div>
              </div>
              {agent.agency_logo && (
                <div style={{ backgroundColor: accentColor, borderRadius: 12, padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <img
                    src={agent.agency_logo}
                    alt={agent.agency_name}
                    style={{ height: 32, objectFit: 'contain' }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        </div>
        )}

        {/* Disclaimer */}
        <div style={{ textAlign: 'center', padding: '24px 0 0' }}>
          <p style={{ fontSize: '0.72rem', color: '#b8b5af', lineHeight: 1.7, maxWidth: 560, margin: '0 auto', letterSpacing: '0.01em' }}>
            This appraisal is an estimate only based on recent comparable sales and the agent's professional opinion. 
            It does not constitute a formal valuation. Market conditions may vary. 
            Please consult a qualified valuer for a formal assessment.
          </p>
        </div>
      </Container>

      {/* Branded Footer Bar — sticky to viewport bottom */}
      {(agent.agency_logo || agent.phone || agent.email) && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: accentColor,
          padding: isMobile ? '14px 20px' : '18px 40px',
          boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
        }}>
          <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {agent.agency_logo && (
                <img src={agent.agency_logo} alt={agent.agency_name || 'Agency'} style={{ height: isMobile ? 28 : 36, objectFit: 'contain' }} />
              )}
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: '0.82rem' }}>
              {agent.phone && (
                <a
                  href={`tel:${agent.phone}`}
                  onClick={() => {
                    trackEvent('click_agent_phone', { phone: agent.phone });
                    handleTokenAndPostRequest(
                      `${window.api_host}/save-settings`,
                      {
                        agent_slug: agent.landing_page_slug || agent.slug || agent.agent_slug,
                        event_type: 'click_agent_phone',
                        metadata: {
                          property_address: appraisal.address || '',
                          appraisal_id: id,
                          phone: agent.phone,
                        },
                      },
                      { 'Content-Type': 'application/json' }
                    ).catch(err => console.error('Error saving click_agent_phone event:', err));
                  }}
                  style={{ color: fontColor, opacity: 0.85, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FaPhone size={11} /> {formatPhoneNumber(agent.phone)}
                </a>
              )}
              {agent.email && (
                <a
                  href={`mailto:${agent.email}`}
                  onClick={() => {
                    trackEvent('click_agent_email', { email: agent.email });
                    handleTokenAndPostRequest(
                      `${window.api_host}/save-settings`,
                      {
                        agent_slug: agent.landing_page_slug || agent.slug || agent.agent_slug,
                        event_type: 'click_agent_email',
                        metadata: {
                          property_address: appraisal.address || '',
                          appraisal_id: id,
                          email: agent.email,
                        },
                      },
                      { 'Content-Type': 'application/json' }
                    ).catch(err => console.error('Error saving click_agent_email event:', err));
                  }}
                  style={{ color: fontColor, opacity: 0.85, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FaEnvelope size={11} /> {agent.email}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppraisalPage;
